import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import produtoService from '../../services/produtoService';
import Toast from '../../components/Toast';
import { withPermissionAndLevel } from '../../components/withPermission';

const initialState = {
  nome: '',
  status: 'Ativo',
  valor: 0,
  tipoProduto: '',
  tipoRequerimento: '',
  tblcontas: '',
};

const ProdutoCreate = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tiposProduto, setTiposProduto] = useState([]);
  const [contas, setContas] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        // Carregar tipos de produto e contas em paralelo
        const [tiposResponse, contasResponse] = await Promise.all([
          produtoService.getTipos(),
          produtoService.getContas()
        ]);
        
        setTiposProduto(tiposResponse.data || []);
        setContas(contasResponse.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setToast({
          show: true,
          message: 'Erro ao carregar dados para o formulário',
          type: 'error'
        });
      }
    };

    loadData();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError('');
    // Limpar erro do campo específico
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  /**
   * Valida no cliente antes de abrir a confirmação.
   * Sem isso o usuário confirmava "Deseja realmente cadastrar este produto?"
   * com o formulário vazio e só descobria os campos faltando depois que o
   * servidor recusava — uma ida ao servidor e uma confirmação inúteis.
   */
  const validar = () => {
    const erros = {};
    const nome = form.nome.trim();

    if (!nome) erros.nome = 'Informe o nome do produto';
    else if (nome.length < 2) erros.nome = 'Nome deve ter pelo menos 2 caracteres';

    if (!form.status) erros.status = 'Selecione o status';
    if (!form.tblcontas) erros.tblcontas = 'Selecione a conta';

    if (form.valor !== '' && form.valor !== null) {
      const valor = Number(form.valor);
      if (Number.isNaN(valor)) erros.valor = 'Valor deve ser um número';
      else if (valor < 0) erros.valor = 'Valor não pode ser negativo';
    }

    return erros;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const erros = validar();
    setFieldErrors(erros);
    if (Object.keys(erros).length > 0) {
      setError('Corrija os campos destacados antes de salvar.');
      return;
    }

    setShowConfirm(true);
  };

  return (
    
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Cadastrar Produto</h2>
      {/* O estado `error` era preenchido mas nunca exibido — o usuário não
          recebia nenhum aviso de que o formulário não pôde ser enviado. */}
      {error && (
        <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.nome ? 'border-red-500' : ''}`}
            placeholder="Nome do produto"
          />
          {fieldErrors.nome && <p className="text-red-500 text-xs mt-1">{fieldErrors.nome}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status *</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.status ? 'border-red-500' : ''}`}
          >
            <option value="">Selecione o status</option>
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
          </select>
          {fieldErrors.status && <p className="text-red-500 text-xs mt-1">{fieldErrors.status}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Valor</label>
          <input
            type="number"
            name="valor"
            value={form.valor}
            onChange={handleChange}
            min={0}
            step={0.01}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.valor ? 'border-red-500' : ''}`}
            placeholder="Valor (R$)"
          />
          {fieldErrors.valor && <p className="text-red-500 text-xs mt-1">{fieldErrors.valor}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo Produto</label>
          <select
            name="tipoProduto"
            value={form.tipoProduto}
            onChange={handleChange}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.tipoProduto ? 'border-red-500' : ''}`}
          >
            <option value="">Selecione o tipo de produto</option>
            {tiposProduto.map(tipo => (
              <option key={tipo.idTipoProduto} value={tipo.idTipoProduto}>
                {tipo.nome}
              </option>
            ))}
          </select>
          {fieldErrors.tipoProduto && <p className="text-red-500 text-xs mt-1">{fieldErrors.tipoProduto}</p>}
        </div>
        <div>
          <label htmlFor="tipoRequerimento" className="block text-sm font-medium mb-1">
            Gera protocolo de requerimento
          </label>
          <select
            id="tipoRequerimento"
            name="tipoRequerimento"
            value={form.tipoRequerimento || ''}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Não gera protocolo</option>
            <option value="CERTIFICADO_HISTORICO">Certificado/Histórico</option>
            <option value="LEVANTAMENTO_NOTAS">Levantamento de Notas</option>
            <option value="CARTEIRINHA_CAPELANIA">Carteirinha - Capelania</option>
            <option value="DECLARACAO">Declaração</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Quando o pedido com este produto recebe baixa, o protocolo do tipo escolhido é gerado automaticamente.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Conta *</label>
          <select
            name="tblcontas"
            value={form.tblcontas}
            onChange={handleChange}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.tblcontas ? 'border-red-500' : ''}`}
          >
            <option value="">Selecione a conta</option>
            {contas.map(conta => (
              <option key={conta.id} value={conta.id}>
                {conta.label}
              </option>
            ))}
          </select>
          {fieldErrors.tblcontas && <p className="text-red-500 text-xs mt-1">{fieldErrors.tblcontas}</p>}
        </div>
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/produtos')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Cadastro"
        message="Deseja realmente cadastrar este produto?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          setLoading(true);
          setError('');
          setSuccess(false);
          try {
            const response = await produtoService.create(form);
            setSuccess(true);
            showToast(response.message || 'Produto criado com sucesso!', 'success');
            setTimeout(() => navigate('/produtos'), 1500);
          } catch (err) {
            console.error('Erro ao cadastrar:', err);
            const errorData = err.response?.data;
            
            // Se houver erros de validação específicos dos campos
            if (errorData?.validationErrors && Array.isArray(errorData.validationErrors)) {
              const errors = {};
              errorData.validationErrors.forEach(error => {
                if (error.path) {
                  // Se já existe um erro para esse campo, mantém o primeiro (mais relevante)
                  if (!errors[error.path]) {
                    errors[error.path] = error.msg;
                  }
                }
              });
              setFieldErrors(errors);
            } else {
              // Erro geral apenas no toast
              showToast(errorData?.message || 'Erro ao cadastrar produto', 'error');
            }
          }
          setLoading(false);
        }}
      />
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
export default withPermissionAndLevel(
  ProdutoCreate, 
  'produtos.create', 
  3, 
  'Você não tem permissão para criar Produtos.'
);
